import React, { useState, useEffect } from "react";
import Image from "next/image";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import httpClient from "@/api/http-client";
import endpoints from "@/api/endpoints";
import { phoneRegExp } from "@/utils/regex";
import styles from "../../styles/shap_of_you.module.scss";
import WelcomeScreenImg from "../../assets/images/WelcomeScreen_dek.png";
import SOYLockUp from "../../assets/images/SOYLockUp.png";
import { SignupScreenProps } from "./types";

const SignupScreen: React.FC<SignupScreenProps> = ({
  navigateToStep,
  authData,
  logStep,
}) => {
  const [sendOtpLoading, setSendOtpLoading] = useState(false);

  // Log signup viewed on mount
  useEffect(() => {
    if (logStep) {
      logStep("signup_viewed");
    }
  }, [logStep]);

  // Register Formik
  const registerFormik = useFormik({
    initialValues: {
      fullName:
        (
          authData.registerData.firstName +
          " " +
          authData.registerData.lastName
        ).trim() || "",
      email: authData.registerData.email || "",
      mobile: authData.mobileNumber || authData.registerData.mobile || "",
      pincode: authData.registerData.pincode || "",
    },
    validationSchema: Yup.object({
      fullName: Yup.string().trim().required("Full name is required"),
      email: Yup.string()
        .required("Email is required")
        .email("Email is invalid"),
      mobile: Yup.string()
        .matches(phoneRegExp, "Mobile number is not valid")
        .test(
          "len",
          "Mobile number needs to be exactly 10 digits",
          (val: any) => val?.toString().length === 10
        )
        .required("Mobile is required"),
      pincode: Yup.string()
        .required("Pincode is required")
        .matches(/^\d{6}$/, "Invalid pincode. Must be a 6-digit number"),
    }),
    onSubmit: async (values) => {
      setSendOtpLoading(true);
      const apiEndpoint = endpoints.auth.sendOtp();
      const requestPayload = {
        mobile: values.mobile,
        email: values.email,
        pincode: values.pincode,
      };

      try {
        const { data } = await httpClient.post(apiEndpoint, requestPayload);

        // Log successful OTP send (non-blocking)
        if (logStep) {
          logStep("signup_otp_sent", {
            api_endpoint: apiEndpoint,
            request_payload: requestPayload,
            response_status: 200,
            response_payload: { otp_uuid: data.otp_uuid },
            success_message: data.message,
          });
        }

        // Success - OTP sent for registration
        const [firstName = "", ...lastNameParts] = values.fullName
          .trim()
          .split(" ");
        const lastName = lastNameParts.join(" ") || "";

        authData.setOtpUid(data.otp_uuid);
        authData.setMobileNumber(values.mobile);
        authData.setAuthFlow("register");
        authData.setRegisterData({
          firstName,
          lastName,
          email: values.email,
          mobile: values.mobile,
          pincode: values.pincode,
        });

        toast(data.message, {
          type: "success",
          toastId: "auth-otp-success",
        });

        navigateToStep("verifyOtp");
      } catch (error: any) {
        // Log API error (non-blocking)
        if (logStep) {
          logStep("signup_otp_sent", {
            api_endpoint: apiEndpoint,
            request_payload: requestPayload,
            response_status: error.response?.status || 500,
            error_message:
              error.response?.data?.message || "Failed to send OTP",
            error_code: error.response?.status?.toString() || "500",
          });
        }

        if (error.response?.data?.message) {
          toast(error.response.data.message, {
            type: "error",
            toastId: "auth-otp-error",
          });
        } else {
          toast("Something went wrong! Try again!", {
            type: "error",
            toastId: "auth-otp-error",
          });
        }
      }
      setSendOtpLoading(false);
    },
  });

  // Update mobile number in formik when it comes from authData
  useEffect(() => {
    if (authData.mobileNumber && !registerFormik.values.mobile) {
      registerFormik.setFieldValue("mobile", authData.mobileNumber);
    }
  }, [authData.mobileNumber]);

  const isFormComplete =
    registerFormik.values.fullName &&
    registerFormik.values.email &&
    registerFormik.values.mobile &&
    registerFormik.values.pincode &&
    registerFormik.isValid;

  return (
    <div className="d-none d-md-block w-100">
      <div className={styles.signup_face_desktop}>
        <div className={styles.bgImage}>
          <Image src={WelcomeScreenImg} alt="Welcome Screen" />
        </div>
        <div className={styles.shapeOfYou}>
          <div className="container">
            <div className="row">
              <div className="col-md-7">
                <div className={styles.content}>
                  <div className={styles.title}>
                    <Image src={SOYLockUp} alt="Shape of You Logo" />
                  </div>
                </div>
              </div>
              <div className="col-md-5">
                <div className={styles.Login_container_form}>
                  <div className={styles.card}>
                    <h1 className={styles.title_m}>Enter Your Details</h1>
                    <p className={styles.subtitle}>
                      Let us get to know you better
                    </p>

                    <form onSubmit={registerFormik.handleSubmit}>
                      {/* Full Name */}
                      <div className={styles.inputBox}>
                        <svg
                          className={styles.icon}
                          width="20"
                          height="20"
                          viewBox="0 0 36 36"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12 10.5C12 12.0913 12.6321 13.6174 13.7574 14.7426C14.8826 15.8679 16.4087 16.5 18 16.5C19.5913 16.5 21.1174 15.8679 22.2426 14.7426C23.3679 13.6174 24 12.0913 24 10.5C24 8.9087 23.3679 7.38258 22.2426 6.25736C21.1174 5.13214 19.5913 4.5 18 4.5C16.4087 4.5 14.8826 5.13214 13.7574 6.25736C12.6321 7.38258 12 8.9087 12 10.5Z"
                            stroke="#262121"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M9 31.5V28.5C9 26.9087 9.63214 25.3826 10.7574 24.2574C11.8826 23.1321 13.4087 22.5 15 22.5H21C22.5913 22.5 24.1174 23.1321 25.2426 24.2574C26.3679 25.3826 27 26.9087 27 28.5V31.5"
                            stroke="#262121"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>

                        <input
                          type="text"
                          placeholder=" Full Name"
                          className={styles.input}
                          value={registerFormik.values.fullName}
                          onChange={(e) =>
                            registerFormik.setFieldValue(
                              "fullName",
                              e.target.value
                            )
                          }
                          onBlur={registerFormik.handleBlur}
                        />
                      </div>
                      {registerFormik.touched.fullName &&
                      registerFormik.errors.fullName ? (
                        <small
                          className="text-danger"
                          style={{
                            display: "block",
                            marginTop: "5px",
                            textAlign: "center",
                          }}
                        >
                          {registerFormik.errors.fullName}
                        </small>
                      ) : null}

                      {/* Phone Number */}
                      <div className={styles.inputBox}>
                        <svg
                          className={styles.icon}
                          width="20"
                          height="20"
                          viewBox="0 0 36 36"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M7.5 6H13.5L16.5 13.5L12.75 15.75C14.3564 19.0073 16.9927 21.6436 20.25 23.25L22.5 19.5L30 22.5V28.5C30 29.2956 29.6839 30.0587 29.1213 30.6213C28.5587 31.1839 27.7956 31.5 27 31.5C21.1489 31.1444 15.6302 28.6597 11.4852 24.5148C7.34026 20.3698 4.85557 14.8511 4.5 9C4.5 8.20435 4.81607 7.44129 5.37868 6.87868C5.94129 6.31607 6.70435 6 7.5 6Z"
                            stroke="#262121"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>

                        <input
                          type="text"
                          placeholder=" Phone number"
                          className={styles.input}
                          maxLength={10}
                          value={registerFormik.values.mobile}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            registerFormik.setFieldValue("mobile", value);
                          }}
                          onBlur={registerFormik.handleBlur}
                        />
                      </div>
                      {registerFormik.touched.mobile &&
                      registerFormik.errors.mobile ? (
                        <small
                          className="text-danger"
                          style={{
                            display: "block",
                            marginTop: "5px",
                            textAlign: "center",
                          }}
                        >
                          {registerFormik.errors.mobile}
                        </small>
                      ) : null}

                      {/* Email ID */}
                      <div className={styles.inputBox}>
                        <svg
                          className={styles.icon}
                          width="20"
                          height="20"
                          viewBox="0 0 36 36"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M4.5 10.5C4.5 9.70435 4.81607 8.94129 5.37868 8.37868C5.94129 7.81607 6.70435 7.5 7.5 7.5H28.5C29.2956 7.5 30.0587 7.81607 30.6213 8.37868C31.1839 8.94129 31.5 9.70435 31.5 10.5V25.5C31.5 26.2956 31.1839 27.0587 30.6213 27.6213C30.0587 28.1839 29.2956 28.5 28.5 28.5H7.5C6.70435 28.5 5.94129 28.1839 5.37868 27.6213C4.81607 27.0587 4.5 26.2956 4.5 25.5V10.5Z"
                            stroke="#262121"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M4.5 10.5L18 19.5L31.5 10.5"
                            stroke="#262121"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>

                        <input
                          type="email"
                          placeholder=" Email Id"
                          className={styles.input}
                          value={registerFormik.values.email}
                          onChange={(e) =>
                            registerFormik.setFieldValue(
                              "email",
                              e.target.value
                            )
                          }
                          onBlur={registerFormik.handleBlur}
                        />
                      </div>
                      {registerFormik.touched.email &&
                      registerFormik.errors.email ? (
                        <small
                          className="text-danger"
                          style={{
                            display: "block",
                            marginTop: "5px",
                            textAlign: "center",
                          }}
                        >
                          {registerFormik.errors.email}
                        </small>
                      ) : null}

                      {/* Pincode */}
                      <div className={styles.inputBox}>
                        <svg
                          className={styles.icon}
                          width="20"
                          height="20"
                          viewBox="0 0 32 32"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12 16C12 17.0609 12.4214 18.0783 13.1716 18.8284C13.9217 19.5786 14.9391 20 16 20C17.0609 20 18.0783 19.5786 18.8284 18.8284C19.5786 18.0783 20 17.0609 20 16C20 14.9391 19.5786 13.9217 18.8284 13.1716C18.0783 12.4214 17.0609 12 16 12C14.9391 12 13.9217 12.4214 13.1716 13.1716C12.4214 13.9217 12 14.9391 12 16Z"
                            stroke="#262121"
                            strokeWidth="2.66667"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M5.3335 16.0002C5.3335 18.8291 6.4573 21.5422 8.45769 23.5426C10.4581 25.543 13.1712 26.6668 16.0002 26.6668C18.8291 26.6668 21.5422 25.543 23.5426 23.5426C25.543 21.5422 26.6668 18.8291 26.6668 16.0002C26.6668 13.1712 25.543 10.4581 23.5426 8.45769C21.5422 6.4573 18.8291 5.3335 16.0002 5.3335C13.1712 5.3335 10.4581 6.4573 8.45769 8.45769C6.4573 10.4581 5.3335 13.1712 5.3335 16.0002Z"
                            stroke="#262121"
                            strokeWidth="2.66667"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M16 2.6665V5.33317"
                            stroke="#262121"
                            strokeWidth="2.66667"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M16 26.6665V29.3332"
                            stroke="#262121"
                            strokeWidth="2.66667"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M26.6665 16H29.3332"
                            stroke="#262121"
                            strokeWidth="2.66667"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M2.6665 16H5.33317"
                            stroke="#262121"
                            strokeWidth="2.66667"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>

                        <input
                          type="number"
                          placeholder=" Pincode"
                          className={styles.input}
                          maxLength={6}
                          value={registerFormik.values.pincode}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            if (value.length <= 6) {
                              registerFormik.setFieldValue("pincode", value);
                            }
                          }}
                          onBlur={registerFormik.handleBlur}
                        />
                      </div>
                      {registerFormik.touched.pincode &&
                      registerFormik.errors.pincode ? (
                        <small
                          className="text-danger"
                          style={{
                            display: "block",
                            marginTop: "5px",
                            textAlign: "center",
                          }}
                        >
                          {registerFormik.errors.pincode}
                        </small>
                      ) : null}

                      <button
                        className={styles.button}
                        type="submit"
                        disabled={sendOtpLoading || !isFormComplete}
                        style={
                          sendOtpLoading || !isFormComplete
                            ? {
                                background: "#d3d3d3",
                                color: "#888",
                                cursor: "not-allowed",
                                opacity: 0.6,
                              }
                            : {
                                background: "#C9302C",
                                color: "white",
                                cursor: "pointer",
                              }
                        }
                      >
                        {sendOtpLoading ? "Sending..." : "Proceed"}{" "}
                        <svg
                          width="25"
                          height="17"
                          viewBox="0 0 30 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M0.000209928 11.0458L0 8.04596H24.2579L18.3333 2.12131L20.4546 0L30.0006 9.54596L20.4546 19.092L18.3333 16.9705L24.258 11.046L0.000209928 11.0458Z"
                            fill="currentcolor"
                          />
                        </svg>
                      </button>
                    </form>
                    <p style={{ textAlign: "center", marginTop: "16px", fontSize: "14px", color: "#262121" }}>
                      Already a registered user?{" "}
                      <span
                        onClick={() => navigateToStep("login")}
                        style={{ color: "#C9302C", cursor: "pointer", fontWeight: 600 }}
                      >
                        Login
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupScreen;
